import React, { useState, Children } from 'react';
import { BookOpenIcon, HeartIcon, SchoolIcon, HomeIcon, ShieldIcon, BookIcon, ChevronDownIcon, SmileIcon, HeartHandshakeIcon, BabyIcon, StarIcon, UserIcon, LightbulbIcon, HandIcon, PencilIcon, ArrowRightIcon } from 'lucide-react';
export function ChildrenRights() {
  const [expandedSection, setExpandedSection] = useState<string | null>('intro');
  const [activeTab, setActiveTab] = useState<string>('all');
  const toggleSection = (section: string) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      setTimeout(() => setExpandedSection(sectionId), 500);
    }
  };
  return <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-12" data-id="element-1085">
      <div className="max-w-4xl mx-auto px-4 sm:px-6" data-id="element-1086">
        {/* Hero Section */}
        <div className="mb-16 text-center" data-id="element-1087">
          <div className="inline-block p-3 bg-amber-100 rounded-full mb-6 animate-bounce-slow" data-id="element-1088">
            <ShieldIcon className="h-10 w-10 text-amber-600" data-id="element-1089" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-amber-900 animate-fade-in" data-id="element-1090">
            حقوق الأبناء في الإسلام
          </h1>
          <p className="text-xl text-amber-700 max-w-2xl mx-auto leading-relaxed" data-id="element-1091">
            "مَنْ لم يَرْحَمْ صَغِيرَنَا وَيَعْرِفْ شَرَفَ كَبِيرِنَا فَلَيْسَ
            مِنَّا"
          </p>
        </div>
        {/* Quick Navigation */}
        <div className="sticky top-4 z-10 mb-8 overflow-hidden" data-id="element-1092">
          <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-3 border border-amber-100" data-id="element-1093">
            <div className="flex justify-center mb-3" data-id="element-1094">
              <h2 className="text-lg font-semibold text-amber-800" data-id="element-1095">
                تصفح سريع
              </h2>
            </div>
            <div className="flex flex-wrap justify-center gap-2 md:gap-3" data-id="element-1096">
              <button onClick={() => scrollToSection('intro')} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${expandedSection === 'intro' ? 'bg-amber-100 text-amber-800' : 'hover:bg-amber-50 text-slate-600'}`} data-id="element-1097">
                المقدمة
              </button>
              <button onClick={() => scrollToSection('play')} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${expandedSection === 'play' ? 'bg-purple-100 text-purple-800' : 'hover:bg-purple-50 text-slate-600'}`} data-id="element-1098">
                اللعب والترفيه
              </button>
              <button onClick={() => scrollToSection('education')} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${expandedSection === 'education' ? 'bg-sky-100 text-sky-800' : 'hover:bg-sky-50 text-slate-600'}`} data-id="element-1099">
                التعليم والتربية
              </button>
              <button onClick={() => scrollToSection('respect')} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${expandedSection === 'respect' ? 'bg-indigo-100 text-indigo-800' : 'hover:bg-indigo-50 text-slate-600'}`} data-id="element-1100">
                الاحترام والهوية
              </button>
              <button onClick={() => scrollToSection('financial')} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${expandedSection === 'financial' ? 'bg-emerald-100 text-emerald-800' : 'hover:bg-emerald-50 text-slate-600'}`} data-id="element-1101">
                الحقوق المالية
              </button>
              <button onClick={() => scrollToSection('emotional')} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${expandedSection === 'emotional' ? 'bg-rose-100 text-rose-800' : 'hover:bg-rose-50 text-slate-600'}`} data-id="element-1102">
                الحقوق العاطفية
              </button>
            </div>
          </div>
        </div>
        {/* Introduction Card */}
        <div id="intro" className="bg-white rounded-2xl shadow-lg p-8 mb-16 border border-amber-100 transform transition-all duration-500 hover:shadow-xl" data-id="element-1103">
          <div className="flex items-center mb-8" data-id="element-1104">
            <div className="bg-amber-100 p-4 rounded-xl" data-id="element-1105">
              <BookOpenIcon className="h-10 w-10 text-amber-600" data-id="element-1106" />
            </div>
            <div className="mr-6" data-id="element-1107">
              <h2 className="text-2xl font-bold text-amber-800 mb-2" data-id="element-1108">
                أهمية رعاية حقوق الأبناء
              </h2>
              <p className="text-amber-600" data-id="element-1109">الأبناء أمانة في أعناق الوالدين</p>
            </div>
          </div>
          <p className="text-slate-700 leading-relaxed mb-8 text-lg" data-id="element-1110">
            الأبناء أمانة في أعناق الوالدين، وقد أولى الإسلام اهتماماً بالغاً
            بحقوق الأبناء وحسن تربيتهم. قال النبي صلى الله عليه وسلم: "كلكم راعٍ
            وكلكم مسؤول عن رعيته"، ومن أعظم الرعاية رعاية الأبناء والقيام
            بحقوقهم المادية والمعنوية.
          </p>
          <div className="bg-amber-50 p-6 rounded-xl border border-amber-200/50 transform transition-all duration-300 hover:scale-102" data-id="element-1111">
            <p className="text-amber-800 leading-relaxed" data-id="element-1112">
              معرفة حقوق الأبناء وتطبيقها بشكل صحيح يساعد في بناء جيل صالح
              متوازن نفسياً وعاطفياً واجتماعياً.
            </p>
          </div>
        </div>
        {/* Main Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16" data-id="element-1113">
          {/* Right to Life */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-amber-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1" data-id="element-1114">
            <div className="p-8" data-id="element-1115">
              <div className="flex items-center mb-6" data-id="element-1116">
                <div className="bg-amber-100 p-4 rounded-xl" data-id="element-1117">
                  <HeartIcon className="h-8 w-8 text-amber-600" data-id="element-1118" />
                </div>
                <div className="mr-4" data-id="element-1119">
                  <h3 className="font-bold text-xl text-amber-800" data-id="element-1120">
                    حق الحياة
                  </h3>
                  <p className="text-amber-600/80 mt-1" data-id="element-1121">الحق الأول للأبناء</p>
                </div>
              </div>
              <p className="text-slate-600 leading-relaxed" data-id="element-1122">
                حرم الإسلام وأد البنات وقتل الأولاد، وجعل لكل إنسان حق الحياة
                والكرامة. قال تعالى: "وَلَا تَقْتُلُوا أَوْلَادَكُمْ خَشْيَةَ
                إِمْلَاقٍ نَحْنُ نَرْزُقُهُمْ وَإِيَّاكُمْ"
              </p>
            </div>
            <div className="bg-amber-50 p-4 border-t border-amber-100" data-id="element-1123">
              <div className="flex justify-end" data-id="element-1124">
                <button className="text-amber-600 hover:text-amber-800 text-sm font-medium flex items-center transition-colors duration-200" data-id="element-1125">
                  <span data-id="element-1126">اقرأ المزيد</span>
                  <ArrowRightIcon className="h-4 w-4 mr-1" data-id="element-1127" />
                </button>
              </div>
            </div>
          </div>
          {/* Right to Lineage */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-emerald-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1" data-id="element-1128">
            <div className="p-8" data-id="element-1129">
              <div className="flex items-center mb-6" data-id="element-1130">
                <div className="bg-emerald-100 p-4 rounded-xl" data-id="element-1131">
                  <BabyIcon className="h-8 w-8 text-emerald-600" data-id="element-1132" />
                </div>
                <div className="mr-4" data-id="element-1133">
                  <h3 className="font-bold text-xl text-emerald-800" data-id="element-1134">
                    حق النسب
                  </h3>
                  <p className="text-emerald-600/80 mt-1" data-id="element-1135">
                    الانتساب إلى الوالدين
                  </p>
                </div>
              </div>
              <p className="text-slate-600 leading-relaxed" data-id="element-1136">
                من حق الطفل أن ينسب إلى والديه الحقيقيين، قال تعالى: "ادْعُوهُمْ
                لِآبَائِهِمْ هُوَ أَقْسَطُ عِنْدَ اللَّهِ" وحرم الإسلام التبني
                بمعنى نسبة الطفل لغير أبيه.
              </p>
            </div>
            <div className="bg-emerald-50 p-4 border-t border-emerald-100" data-id="element-1137">
              <div className="flex justify-end" data-id="element-1138">
                <button className="text-emerald-600 hover:text-emerald-800 text-sm font-medium flex items-center transition-colors duration-200" data-id="element-1139">
                  <span data-id="element-1140">اقرأ المزيد</span>
                  <ArrowRightIcon className="h-4 w-4 mr-1" data-id="element-1141" />
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* NEW SECTION: Right to Play and Recreation */}
        <div id="play" className="bg-white rounded-xl shadow-lg p-8 mb-16 border border-purple-100 transform transition-all duration-500 hover:shadow-xl" data-id="element-1142">
          <div className="flex items-center mb-8" data-id="element-1143">
            <div className="bg-purple-100 p-4 rounded-xl animate-pulse-slow" data-id="element-1144">
              <SmileIcon className="h-10 w-10 text-purple-600" data-id="element-1145" />
            </div>
            <div className="mr-6" data-id="element-1146">
              <h2 className="text-2xl font-bold text-purple-800 mb-2" data-id="element-1147">
                حق اللعب والترفيه
              </h2>
              <p className="text-purple-600" data-id="element-1148">تنمية المهارات من خلال اللعب</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8" data-id="element-1149">
            <div className="bg-purple-50/50 p-6 rounded-xl border border-purple-100 transition-all duration-300 hover:bg-purple-50" data-id="element-1150">
              <h4 className="font-bold text-lg text-purple-700 mb-4 flex items-center" data-id="element-1151">
                <SmileIcon className="h-5 w-5 ml-2 text-purple-500" data-id="element-1152" />
                أهمية اللعب في الإسلام
              </h4>
              <p className="text-slate-600 mb-4 leading-relaxed" data-id="element-1153">
                أقر الإسلام حق الطفل في اللعب والترفيه، وكان النبي صلى الله عليه
                وسلم يمازح الأطفال ويلاعبهم. فاللعب ضروري لتنمية مهارات الطفل
                الجسدية والعقلية والاجتماعية.
              </p>
              <p className="text-slate-600 leading-relaxed" data-id="element-1154">
                كان النبي صلى الله عليه وسلم يحمل الحسن والحسين على ظهره ويقول:
                "نعم الجمل جملكما ونعم العدلان أنتما"، وكان يسابق عائشة رضي الله
                عنها.
              </p>
            </div>
            <div className="bg-purple-50/50 p-6 rounded-xl border border-purple-100 transition-all duration-300 hover:bg-purple-50" data-id="element-1155">
              <h4 className="font-bold text-lg text-purple-700 mb-4 flex items-center" data-id="element-1156">
                <ShieldIcon className="h-5 w-5 ml-2 text-purple-500" data-id="element-1157" />
                الضوابط الشرعية للعب
              </h4>
              <ul className="space-y-3" data-id="element-1158">
                <li className="flex items-start group" data-id="element-1159">
                  <span className="ml-2 flex-shrink-0 h-2 w-2 rounded-full bg-purple-500 mt-2 group-hover:scale-125 transition-transform duration-300" data-id="element-1160"></span>
                  <span className="text-slate-600 group-hover:text-purple-700 transition-colors duration-200" data-id="element-1161">
                    أن يكون اللعب خالياً من المحرمات كالميسر والقمار
                  </span>
                </li>
                <li className="flex items-start group" data-id="element-1162">
                  <span className="ml-2 flex-shrink-0 h-2 w-2 rounded-full bg-purple-500 mt-2 group-hover:scale-125 transition-transform duration-300" data-id="element-1163"></span>
                  <span className="text-slate-600 group-hover:text-purple-700 transition-colors duration-200" data-id="element-1164">
                    ألا يؤدي اللعب إلى إيذاء الطفل أو غيره من الأطفال
                  </span>
                </li>
                <li className="flex items-start group" data-id="element-1165">
                  <span className="ml-2 flex-shrink-0 h-2 w-2 rounded-full bg-purple-500 mt-2 group-hover:scale-125 transition-transform duration-300" data-id="element-1166"></span>
                  <span className="text-slate-600 group-hover:text-purple-700 transition-colors duration-200" data-id="element-1167">
                    ألا يشغل اللعب الطفل عن واجباته الدينية والدراسية
                  </span>
                </li>
                <li className="flex items-start group" data-id="element-1168">
                  <span className="ml-2 flex-shrink-0 h-2 w-2 rounded-full bg-purple-500 mt-2 group-hover:scale-125 transition-transform duration-300" data-id="element-1169"></span>
                  <span className="text-slate-600 group-hover:text-purple-700 transition-colors duration-200" data-id="element-1170">
                    أن يكون اللعب هادفاً ومفيداً قدر الإمكان
                  </span>
                </li>
              </ul>
            </div>
          </div>
          <div className="bg-purple-50 p-6 rounded-xl border border-purple-200/50 transform transition-all duration-300 hover:scale-102" data-id="element-1171">
            <div className="flex items-start" data-id="element-1172">
              <StarIcon className="h-6 w-6 text-purple-500 ml-4 flex-shrink-0 mt-1" data-id="element-1173" />
              <p className="text-purple-700 leading-relaxed" data-id="element-1174">
                اللعب المتوازن يساعد في بناء شخصية سوية للطفل، ويعزز قدراته
                الإبداعية والاجتماعية. وقد أكدت الدراسات الحديثة ما أقره الإسلام
                منذ قرون من أهمية اللعب في نمو الأطفال وتطورهم.
              </p>
            </div>
          </div>
        </div>
        {/* Education and Care Section */}
        <div id="education" className="bg-white rounded-xl shadow-lg p-8 mb-16 border border-sky-100 transform transition-all duration-500 hover:shadow-xl" data-id="element-1175">
          <div className="flex items-center mb-8" data-id="element-1176">
            <div className="bg-sky-100 p-4 rounded-xl" data-id="element-1177">
              <SchoolIcon className="h-10 w-10 text-sky-600" data-id="element-1178" />
            </div>
            <div className="mr-6" data-id="element-1179">
              <h2 className="text-2xl font-bold text-sky-800 mb-2" data-id="element-1180">
                حق التعليم والتربية
              </h2>
              <p className="text-sky-600" data-id="element-1181">بناء شخصية متوازنة للطفل</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8" data-id="element-1182">
            <div className="bg-sky-50/50 p-6 rounded-xl border border-sky-100 transition-all duration-300 hover:bg-sky-50" data-id="element-1183">
              <h4 className="font-bold text-lg text-sky-700 mb-4 flex items-center" data-id="element-1184">
                <BookIcon className="h-5 w-5 ml-2 text-sky-500" data-id="element-1185" />
                التعليم الديني
              </h4>
              <ul className="space-y-3" data-id="element-1186">
                <li className="flex items-start group" data-id="element-1187">
                  <span className="ml-2 flex-shrink-0 h-2 w-2 rounded-full bg-sky-500 mt-2 group-hover:scale-125 transition-transform duration-300" data-id="element-1188"></span>
                  <span className="text-slate-600 group-hover:text-sky-700 transition-colors duration-200" data-id="element-1189">
                    تعليم الطفل أصول الدين والعقيدة الصحيحة
                  </span>
                </li>
                <li className="flex items-start group" data-id="element-1190">
                  <span className="ml-2 flex-shrink-0 h-2 w-2 rounded-full bg-sky-500 mt-2 group-hover:scale-125 transition-transform duration-300" data-id="element-1191"></span>
                  <span className="text-slate-600 group-hover:text-sky-700 transition-colors duration-200" data-id="element-1192">
                    تحفيظه القرآن الكريم والأحاديث النبوية
                  </span>
                </li>
                <li className="flex items-start group" data-id="element-1193">
                  <span className="ml-2 flex-shrink-0 h-2 w-2 rounded-full bg-sky-500 mt-2 group-hover:scale-125 transition-transform duration-300" data-id="element-1194"></span>
                  <span className="text-slate-600 group-hover:text-sky-700 transition-colors duration-200" data-id="element-1195">
                    تعليمه الصلاة والعبادات في سن مبكرة
                  </span>
                </li>
              </ul>
            </div>
            <div className="bg-sky-50/50 p-6 rounded-xl border border-sky-100 transition-all duration-300 hover:bg-sky-50" data-id="element-1196">
              <h4 className="font-bold text-lg text-sky-700 mb-4 flex items-center" data-id="element-1197">
                <SchoolIcon className="h-5 w-5 ml-2 text-sky-500" data-id="element-1198" />
                التعليمدنيوي
              </h4>
              <ul className="space-y-3" data-id="element-1199">
                <li className="flex items-start group" data-id="element-1200">
                  <span className="ml-2 flex-shrink-0 h-2 w-2 rounded-full bg-sky-500 mt-2 group-hover:scale-125 transition-transform duration-300" data-id="element-1201"></span>
                  <span className="text-slate-600 group-hover:text-sky-700 transition-colors duration-200" data-id="element-1202">
                    توفير التعليم المناسب لقدرات الطفل
                  </span>
                </li>
                <li className="flex items-start group" data-id="element-1203">
                  <span className="ml-2 flex-shrink-0 h-2 w-2 rounded-full bg-sky-500 mt-2 group-hover:scale-125 transition-transform duration-300" data-id="element-1204"></span>
                  <span className="text-slate-600 group-hover:text-sky-700 transition-colors duration-200" data-id="element-1205">
                    تنمية مواهبه وقدراته الفكرية
                  </span>
                </li>
                <li className="flex items-start group" data-id="element-1206">
                  <span className="ml-2 flex-shrink-0 h-2 w-2 rounded-full bg-sky-500 mt-2 group-hover:scale-125 transition-transform duration-300" data-id="element-1207"></span>
                  <span className="text-slate-600 group-hover:text-sky-700 transition-colors duration-200" data-id="element-1208">
                    تشجيعه على طلب العلم والمعرفة
                  </span>
                </li>
              </ul>
            </div>
          </div>
          <div className="bg-sky-50 p-6 rounded-xl border border-sky-200/50 transform transition-all duration-300 hover:scale-102" data-id="element-1209">
            <div className="flex items-start" data-id="element-1210">
              <StarIcon className="h-6 w-6 text-sky-500 ml-4 flex-shrink-0 mt-1" data-id="element-1211" />
              <p className="text-sky-700 leading-relaxed" data-id="element-1212">
                قال النبي صلى الله عليه وسلم: "ما نحل والد ولدا من نحل أفضل من
                أدب حسن"، وكان السلف الصالح يهتمون بتعليم أولادهم وتأديبهم قبل
                العلوم الدنيوية.
              </p>
            </div>
          </div>
        </div>
        {/* NEW SECTION: Right to Respect and Individual Identity */}
        <div id="respect" className="bg-white rounded-xl shadow-lg p-8 mb-16 border border-indigo-100 transform transition-all duration-500 hover:shadow-xl" data-id="element-1213">
          <div className="flex items-center mb-8" data-id="element-1214">
            <div className="bg-indigo-100 p-4 rounded-xl" data-id="element-1215">
              <HandIcon className="h-10 w-10 text-indigo-600" data-id="element-1216" />
            </div>
            <div className="mr-6" data-id="element-1217">
              <h2 className="text-2xl font-bold text-indigo-800 mb-2" data-id="element-1218">
                حق الاحترام والهوية الفردية
              </h2>
              <p className="text-indigo-600" data-id="element-1219">تقدير شخصية الطفل وكرامته</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8" data-id="element-1220">
            <div className="bg-indigo-50/50 p-6 rounded-xl border border-indigo-100 transition-all duration-300 hover:bg-indigo-50" data-id="element-1221">
              <h4 className="font-bold text-lg text-indigo-700 mb-4 flex items-center" data-id="element-1222">
                <UserIcon className="h-5 w-5 ml-2 text-indigo-500" data-id="element-1223" />
                احترام شخصية الطفل
              </h4>
              <p className="text-slate-600 mb-4 leading-relaxed" data-id="element-1224">
                من حق الطفل أن يُعامل باحترام وتقدير، وأن يُنظر إليه كإنسان له
                كرامته وشخصيته المستقلة. وكان النبي صلى الله عليه وسلم يستأذن
                الأطفال ويستشيرهم في بعض الأمور، ويخاطبهم بألطف الخطاب.
              </p>
              <p className="text-slate-600 leading-relaxed" data-id="element-1225">
                قال صلى الله عليه وسلم: "أكرموا أولادكم وأحسنوا أدبهم"، وكان
                يسلم على الصبيان إذا مر بهم، مم�� يدل على تقديره لهم واحترامه
                لشخصياتهم.
              </p>
            </div>
            <div className="bg-indigo-50/50 p-6 rounded-xl border border-indigo-100 transition-all duration-300 hover:bg-indigo-50" data-id="element-1226">
              <h4 className="font-bold text-lg text-indigo-700 mb-4 flex items-center" data-id="element-1227">
                <HandIcon className="h-5 w-5 ml-2 text-indigo-500" data-id="element-1228" />
                مظاهر احترام شخصية الطفل
              </h4>
              <ul className="space-y-3" data-id="element-1229">
                <li className="flex items-start group" data-id="element-1230">
                  <span className="ml-2 flex-shrink-0 h-2 w-2 rounded-full bg-indigo-500 mt-2 group-hover:scale-125 transition-transform duration-300" data-id="element-1231"></span>
                  <span className="text-slate-600 group-hover:text-indigo-700 transition-colors duration-200" data-id="element-1232">
                    الاستماع إليه وإعطاؤه فرصة للتعبير عن رأيه
                  </span>
                </li>
                <li className="flex items-start group" data-id="element-1233">
                  <span className="ml-2 flex-shrink-0 h-2 w-2 rounded-full bg-indigo-500 mt-2 group-hover:scale-125 transition-transform duration-300" data-id="element-1234"></span>
                  <span className="text-slate-600 group-hover:text-indigo-700 transition-colors duration-200" data-id="element-1235">
                    عدم السخرية منه أو التقليل من شأنه أمام الآخرين
                  </span>
                </li>
                <li className="flex items-start group" data-id="element-1236">
                  <span className="ml-2 flex-shrink-0 h-2 w-2 rounded-full bg-indigo-500 mt-2 group-hover:scale-125 transition-transform duration-300" data-id="element-1237"></span>
                  <span className="text-slate-600 group-hover:text-indigo-700 transition-colors duration-200" data-id="element-1238">
                    مناداته بأحب الأسماء إليه وتجنب الألقاب السيئة
                  </span>
                </li>
                <li className="flex items-start group" data-id="element-1239">
                  <span className="ml-2 flex-shrink-0 h-2 w-2 rounded-full bg-indigo-500 mt-2 group-hover:scale-125 transition-transform duration-300" data-id="element-1240"></span>
                  <span className="text-slate-600 group-hover:text-indigo-700 transition-colors duration-200" data-id="element-1241">
                    احترام خصوصيته وممتلكاته الشخصية
                  </span>
                </li>
              </ul>
            </div>
          </div>
          <div className="bg-indigo-50 p-6 rounded-xl border border-indigo-200/50 transform transition-all duration-300 hover:scale-102" data-id="element-1242">
            <div className="flex items-start" data-id="element-1243">
              <PencilIcon className="h-6 w-6 text-indigo-500 ml-4 flex-shrink-0 mt-1" data-id="element-1244" />
              <p className="text-indigo-700 leading-relaxed" data-id="element-1245">
                الطفل الذي ينشأ في بيئة تحترم شخصيته وتقدر آراءه يكون أكثر ثقة
                بنفسه، وأقدر على تحمل المسؤولية، وأكثر احتراماً للآخرين. وهذا من
                أهم أسس بناء الشخصية السوية المتوازنة.
              </p>
            </div>
          </div>
        </div>
        {/* Financial Rights Section */}
        <div id="financial" className="bg-white rounded-xl shadow-lg p-8 mb-16 border border-emerald-100 transform transition-all duration-500 hover:shadow-xl" data-id="element-1246">
          <div className="flex items-center mb-8" data-id="element-1247">
            <div className="bg-emerald-100 p-4 rounded-xl" data-id="element-1248">
              <HomeIcon className="h-10 w-10 text-emerald-600" data-id="element-1249" />
            </div>
            <div className="mr-6" data-id="element-1250">
              <h2 className="text-2xl font-bold text-emerald-800 mb-2" data-id="element-1251">
                الحقوق المالية للأبناء
              </h2>
              <p className="text-emerald-600" data-id="element-1252">النفقة والإنفاق والميراث</p>
            </div>
          </div>
          <div className="space-y-6 mb-8" data-id="element-1253">
            <div className="bg-emerald-50/50 p-6 rounded-xl border border-emerald-100 transition-all duration-300 hover:bg-emerald-50" data-id="element-1254">
              <h4 className="font-bold text-lg text-emerald-700 mb-3 flex items-center" data-id="element-1255">
                <HomeIcon className="h-5 w-5 ml-2 text-emerald-500" data-id="element-1256" />
                النفقة الواجبة
              </h4>
              <p className="text-slate-600 leading-relaxed" data-id="element-1257">
                يجب على الأب الإنفاق على أبنائه وتوفير المأكل والمشرب والملبس
                والمسكن المناسب لهم حتى يبلغوا سن الرشد، وعلى البنات حتى يتزوجن.
                قال تعالى: "وَعَلَى الْمَوْلُودِ لَهُ رِزْقُهُنَّ
                وَكِسْوَتُهُنَّ بِالْمَعْرُوفِ"
              </p>
            </div>
            <div className="bg-emerald-50/50 p-6 rounded-xl border border-emerald-100 transition-all duration-300 hover:bg-emerald-50" data-id="element-1258">
              <h4 className="font-bold text-lg text-emerald-700 mb-3 flex items-center" data-id="element-1259">
                <BookIcon className="h-5 w-5 ml-2 text-emerald-500" data-id="element-1260" />
                حق الميراث
              </h4>
              <p className="text-slate-600 leading-relaxed" data-id="element-1261">
                للأبناء حق في ميراث والديهم بعد وفاتهم حسب الأنصبة الشرعية
                المحددة في القرآن الكريم، ولا يجوز حرمانهم من الميراث أو الإنقاص
                من أنصبتهم.
              </p>
            </div>
          </div>
          <div className="p-6 bg-amber-50 rounded-xl border border-amber-200/50 transform transition-all duration-300 hover:scale-102" data-id="element-1262">
            <div className="flex items-start" data-id="element-1263">
              <StarIcon className="h-6 w-6 text-amber-500 ml-4 flex-shrink-0 mt-1" data-id="element-1264" />
              <p className="text-amber-700 leading-relaxed" data-id="element-1265">
                من الأفضل أن يعدل الوالدان بين أبنائهم في العطايا والهبات في
                حياتهم، فقد قال النبي صلى الله عليه وسلم: "اتقوا الله واعدلوا
                بين أولادكم"
              </p>
            </div>
          </div>
        </div>
        {/* Emotional Rights Section */}
        <div id="emotional" className="bg-white rounded-xl shadow-lg p-8 mb-16 border border-rose-100 transform transition-all duration-500 hover:shadow-xl" data-id="element-1266">
          <div className="flex items-center mb-8" data-id="element-1267">
            <div className="bg-rose-100 p-4 rounded-xl" data-id="element-1268">
              <HeartHandshakeIcon className="h-10 w-10 text-rose-600" data-id="element-1269" />
            </div>
            <div className="mr-6" data-id="element-1270">
              <h2 className="text-2xl font-bold text-rose-800 mb-2" data-id="element-1271">
                الحقوق العاطفية والنفسية
              </h2>
              <p className="text-rose-600" data-id="element-1272">الحب والرحمة والعدل بين الأبناء</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-1273">
            <div className="bg-rose-50/50 p-6 rounded-xl border border-rose-100 transition-all duration-300 hover:bg-rose-50 transform hover:scale-105" data-id="element-1274">
              <div className="flex items-center justify-center mb-4" data-id="element-1275">
                <HeartIcon className="h-8 w-8 text-rose-500" data-id="element-1276" />
              </div>
              <h4 className="font-bold text-lg text-rose-700 mb-3 text-center" data-id="element-1277">
                الحب والحنان
              </h4>
              <p className="text-slate-600 text-center" data-id="element-1278">
                من حق الطفل أن يشعر بحب والديه له، وأن يعبرا له عن هذا الحب
                بالقول والفعل
              </p>
            </div>
            <div className="bg-rose-50/50 p-6 rounded-xl border border-rose-100 transition-all duration-300 hover:bg-rose-50 transform hover:scale-105" data-id="element-1279">
              <div className="flex items-center justify-center mb-4" data-id="element-1280">
                <SmileIcon className="h-8 w-8 text-rose-500" data-id="element-1281" />
              </div>
              <h4 className="font-bold text-lg text-rose-700 mb-3 text-center" data-id="element-1282">
                الرحمة والرفق
              </h4>
              <p className="text-slate-600 text-center" data-id="element-1283">
                الرحمة بالأبناء والرفق بهم من هدي النبي صلى الله عليه وسلم في
                التعامل مع الأطفال
              </p>
            </div>
            <div className="bg-rose-50/50 p-6 rounded-xl border border-rose-100 transition-all duration-300 hover:bg-rose-50 transform hover:scale-105" data-id="element-1284">
              <div className="flex items-center justify-center mb-4" data-id="element-1285">
                <UserIcon className="h-8 w-8 text-rose-500" data-id="element-1286" />
              </div>
              <h4 className="font-bold text-lg text-rose-700 mb-3 text-center" data-id="element-1287">
                العدل والمساواة
              </h4>
              <p className="text-slate-600 text-center" data-id="element-1288">
                العدل بين الأبناء في المعاملة والعطايا واجب على الوالدين، وعدم
                التفريق بينهم
              </p>
            </div>
          </div>
          <div className="p-6 bg-rose-50 rounded-xl transition-all duration-300 hover:bg-rose-100" data-id="element-1289">
            <p className="text-slate-600 leading-relaxed text-center" data-id="element-1290">
              قال النبي صلى الله عليه وسلم: "ما نحل والد ولدا أفضل من أدب حسن"،
              وكان يقبل الحسن والحسين ويظهر حبه لهما، وعندما سأله الأقرع بن
              حابس: "أتقبلون صبيانكم؟ فوالله إن لي عشرة من الولد ما قبلت منهم
              أحدا"، فقال صلى الله عليه وسلم: "أو أملك لك أن نزع الله من قلبك
              الرحمة".
            </p>
          </div>
        </div>
        {/* NEW SECTION: Practical Applications */}
        <div id="practical" className="bg-white rounded-xl shadow-lg p-8 mb-16 border border-teal-100 transform transition-all duration-500 hover:shadow-xl" data-id="element-1291">
          <div className="flex items-center mb-8" data-id="element-1292">
            <div className="bg-teal-100 p-4 rounded-xl" data-id="element-1293">
              <LightbulbIcon className="h-10 w-10 text-teal-600" data-id="element-1294" />
            </div>
            <div className="mr-6" data-id="element-1295">
              <h2 className="text-2xl font-bold text-teal-800 mb-2" data-id="element-1296">
                تطبيقات عملية لحقوق الأبناء
              </h2>
              <p className="text-teal-600" data-id="element-1297">
                كيف نطبق هذه الحقوق في حياتنا اليومية
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8" data-id="element-1298">
            <div className="bg-teal-50/50 p-6 rounded-xl border border-teal-100 transition-all duration-300 hover:bg-teal-50" data-id="element-1299">
              <h4 className="font-bold text-lg text-teal-700 mb-4 flex items-center" data-id="element-1300">
                <LightbulbIcon className="h-5 w-5 ml-2 text-teal-500" data-id="element-1301" />
                تطبيقات يومية للآباء
              </h4>
              <ul className="space-y-3" data-id="element-1302">
                <li className="flex items-start group" data-id="element-1303">
                  <span className="ml-2 flex-shrink-0 h-2 w-2 rounded-full bg-teal-500 mt-2 group-hover:scale-125 transition-transform duration-300" data-id="element-1304"></span>
                  <span className="text-slate-600 group-hover:text-teal-700 transition-colors duration-200" data-id="element-1305">
                    تخصيص وقت يومي للجلوس مع الأبناء والاستماع إليهم
                  </span>
                </li>
                <li className="flex items-start group" data-id="element-1306">
                  <span className="ml-2 flex-shrink-0 h-2 w-2 rounded-full bg-teal-500 mt-2 group-hover:scale-125 transition-transform duration-300" data-id="element-1307"></span>
                  <span className="text-slate-600 group-hover:text-teal-700 transition-colors duration-200" data-id="element-1308">
                    التعبير المنتظم عن الحب والتقدير للطفل بالكلمات والأفعال
                  </span>
                </li>
                <li className="flex items-start group" data-id="element-1309">
                  <span className="ml-2 flex-shrink-0 h-2 w-2 rounded-full bg-teal-500 mt-2 group-hover:scale-125 transition-transform duration-300" data-id="element-1310"></span>
                  <span className="text-slate-600 group-hover:text-teal-700 transition-colors duration-200" data-id="element-1311">
                    إشراك الأبناء في القرارات العائلية المناسبة لسنهم
                  </span>
                </li>
                <li className="flex items-start group" data-id="element-1312">
                  <span className="ml-2 flex-shrink-0 h-2 w-2 rounded-full bg-teal-500 mt-2 group-hover:scale-125 transition-transform duration-300" data-id="element-1313"></span>
                  <span className="text-slate-600 group-hover:text-teal-700 transition-colors duration-200" data-id="element-1314">
                    تعليم الأبناء مهارات الحياة اليومية المناسبة لأعمارهم
                  </span>
                </li>
              </ul>
            </div>
            <div className="bg-teal-50/50 p-6 rounded-xl border border-teal-100 transition-all duration-300 hover:bg-teal-50" data-id="element-1315">
              <h4 className="font-bold text-lg text-teal-700 mb-4 flex items-center" data-id="element-1316">
                <HeartHandshakeIcon className="h-5 w-5 ml-2 text-teal-500" data-id="element-1317" />
                أنشطة تعزز حقوق الأبناء
              </h4>
              <ul className="space-y-3" data-id="element-1318">
                <li className="flex items-start group" data-id="element-1319">
                  <span className="ml-2 flex-shrink-0 h-2 w-2 rounded-full bg-teal-500 mt-2 group-hover:scale-125 transition-transform duration-300" data-id="element-1320"></span>
                  <span className="text-slate-600 group-hover:text-teal-700 transition-colors duration-200" data-id="element-1321">
                    تخصيص يوم أسبوعي للنشاط العائلي المشترك
                  </span>
                </li>
                <li className="flex items-start group" data-id="element-1322">
                  <span className="ml-2 flex-shrink-0 h-2 w-2 rounded-full bg-teal-500 mt-2 group-hover:scale-125 transition-transform duration-300" data-id="element-1323"></span>
                  <span className="text-slate-600 group-hover:text-teal-700 transition-colors duration-200" data-id="element-1324">
                    إنشاء مكتبة منزلية تناسب اهتمامات الأبناء وأعمارهم
                  </span>
                </li>
                <li className="flex items-start group" data-id="element-1325">
                  <span className="ml-2 flex-shrink-0 h-2 w-2 rounded-full bg-teal-500 mt-2 group-hover:scale-125 transition-transform duration-300" data-id="element-1326"></span>
                  <span className="text-slate-600 group-hover:text-teal-700 transition-colors duration-200" data-id="element-1327">
                    تشجيع الأبناء على ممارسة الهوايات المفيدة وتوفير أدواتها
                  </span>
                </li>
                <li className="flex items-start group" data-id="element-1328">
                  <span className="ml-2 flex-shrink-0 h-2 w-2 rounded-full bg-teal-500 mt-2 group-hover:scale-125 transition-transform duration-300" data-id="element-1329"></span>
                  <span className="text-slate-600 group-hover:text-teal-700 transition-colors duration-200" data-id="element-1330">
                    تنظيم زيارات للأقارب وتعزيز صلة الرحم
                  </span>
                </li>
              </ul>
            </div>
          </div>
          <div className="bg-teal-50 p-6 rounded-xl border border-teal-200/50 transform transition-all duration-300 hover:scale-102" data-id="element-1331">
            <div className="flex items-start" data-id="element-1332">
              <StarIcon className="h-6 w-6 text-teal-500 ml-4 flex-shrink-0 mt-1" data-id="element-1333" />
              <p className="text-teal-700 leading-relaxed" data-id="element-1334">
                التطبيق العملي لحقوق الأبناء في الحياة اليومية يحتاج إلى وعي
                ومتابعة مستمرة من الوالدين. ومن المهم أن يكون الوالدان قدوة
                لأبنائهم في التعامل الحسن واحترام حقوق الآخرين.
              </p>
            </div>
          </div>
        </div>
        {/* Conclusion Section */}
        <div className="bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-2xl shadow-xl p-10 transform transition-all duration-500 hover:shadow-2xl hover:from-amber-700 hover:to-amber-800" data-id="element-1335">
          <h3 className="text-2xl font-bold mb-6 text-center" data-id="element-1336">ختاماً</h3>
          <div className="max-w-2xl mx-auto" data-id="element-1337">
            <p className="mb-8 text-center leading-relaxed text-lg" data-id="element-1338">
              رعاية حقوق الأبناء من أعظم الأعمال التي يتقرب بها العبد إلى الله
              تعالى. وهي استثمار للدنيا والآخرة، فالابن الصالح من أعظم ما يبقى
              للإنسان بعد موته.
            </p>
            <div className="w-20 h-0.5 bg-white/30 mx-auto mb-8" data-id="element-1339" />
            <p className="text-center text-white/90 italic text-lg" data-id="element-1340">
              قال النبي صلى الله عليه وسلم: "إذا مات ابن آدم انقطع عمله إلا من
              ثلاث: صدقة جارية، أو علم ينتفع به، أو ولد صالح يدعو له"
            </p>
          </div>
        </div>
      </div>
    </div>;
}
// Add these animations to your global CSS
// @keyframes bounce-slow {
//   0%, 100% { transform: translateY(0); }
//   50% { transform: translateY(-10px); }
// }
// @keyframes pulse-slow {
//   0%, 100% { opacity: 1; }
//   50% { opacity: 0.8; }
// }
// @keyframes fade-in {
//   0% { opacity: 0; }
//   100% { opacity: 1; }
// }
// .animate-bounce-slow {
//   animation: bounce-slow 3s ease-in-out infinite;
// }
// .animate-pulse-slow {
//   animation: pulse-slow 3s ease-in-out infinite;
// }
// .animate-fade-in {
//   animation: fade-in 1.5s ease-out;
// }
// .hover\:scale-102:hover {
//   transform: scale(1.02);
// }